/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { listNews } from '../../api/api';
import { formatStoredDate } from '../../utils/datetime';

/**
 * Strips HTML tags and decodes HTML entities from a string
 * @param {string} html - HTML string to clean
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  if (!html) return '';
  // Create a temporary DOM element to parse HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Get text content (strips all tags)
  let text = doc.body.textContent || '';
  // Clean up extra whitespace
  text = text.replace(/\s+/g, ' ').trim();
  return text;
};

/**
 * Truncates text to a maximum length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum character length
 * @returns {string} Truncated text
 */
const truncateText = (text, maxLength = 200) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

/**
 * NewsWidget - Displays financial news feed
 * 
 * Props:
 * - hideTitle: boolean - hide widget title
 * - limit: number - max news items to display
 * - autoRefreshMs: number - auto refresh interval (0 to disable)
 * - source: string - filter by news source
 */
const NewsWidget = ({ 
  hideTitle = false, 
  limit = 20,
  autoRefreshMs = 60000,
  source = null 
}) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (source) params.source = source;
      const data = await listNews(params);
      const newsArray = Array.isArray(data) ? data : (data?.results || []);
      setNews(newsArray.slice(0, limit));
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [source, limit]);

  useEffect(() => {
    fetchNews();
    
    if (autoRefreshMs > 0) {
      const interval = setInterval(fetchNews, autoRefreshMs);
      return () => clearInterval(interval);
    }
  }, [fetchNews, autoRefreshMs]);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatStoredDate(timeStr, undefined, '-');
  };

  return (
    <Widget title="News" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header with refresh */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-borderDefault">
          <span className="text-xs text-textSecondary">
            {news.length} articles
          </span>
          <button
            onClick={fetchNews}
            disabled={loading}
            className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* News list */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="p-4 text-center text-destructive text-sm">{error}</div>
          )}
          
          {!error && news.length === 0 && !loading && (
            <div className="p-4 text-center text-textSecondary text-sm">No news available</div>
          )}

          {!error && news.map((item, idx) => (
            <div
              key={item.id || idx}
              className="p-2 sm:p-3 border-b border-borderDefault hover:bg-secondary/10 transition-colors cursor-pointer"
              onClick={() => item.url && window.open(item.url, '_blank')}
            >
              <div className="flex items-start justify-between gap-1 sm:gap-2">
                <h4 className="text-xs sm:text-sm font-medium text-textPrimary line-clamp-2 flex-1">
                  {stripHtml(item.headline || item.title)}
                </h4>
                <span className="text-[10px] sm:text-xs text-textSecondary whitespace-nowrap flex-shrink-0">
                  {formatTime(item.time || item.published_at)}
                </span>
              </div>
              {item.source && (
                <div className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-textSecondary">
                  {item.source}
                </div>
              )}
              {item.content && (
                <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-textSecondary line-clamp-2">
                  {truncateText(stripHtml(item.content), 150)}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </Widget>
  );
};

NewsWidget.propTypes = {
  hideTitle: PropTypes.bool,
  limit: PropTypes.number,
  autoRefreshMs: PropTypes.number,
  source: PropTypes.string,
};

export default NewsWidget;
